import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const SignatureDishes = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const carouselRef = useRef(null);

  const signatureDishes = [
    {
      id: 1,
      name: "Lamb Shawarma Platter",
      description: "Tender lamb marinated in Mediterranean spices, slow-roasted to perfection and served with fresh pita, hummus, and our signature garlic sauce.",
      price: "$18.99",
      image: "https://images.pexels.com/photos/4253302/pexels-photo-4253302.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      chef: "Chef Omar",
      chefNote: "My grandmother\'s secret blend of 12 spices makes this our most beloved dish",
      dietary: ["Halal", "Protein Rich"],
      prepTime: "15 min",
      spiceLevel: 2,
      isSignature: true
    },
    {
      id: 2,
      name: "Chicken Kebab Feast",
      description: "Juicy chicken breast cubes grilled over open flames, accompanied by saffron rice, grilled vegetables, and tzatziki sauce.",
      price: "$16.99",
      image: "https://images.pexels.com/photos/5474640/pexels-photo-5474640.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      chef: "Chef Amira",
      chefNote: "Marinated for 24 hours in yogurt and herbs for maximum tenderness",
      dietary: ["Halal", "High Protein", "Gluten Free"],
      prepTime: "12 min",
      spiceLevel: 1,
      isSignature: true
    },
    {
      id: 3,
      name: "Mediterranean Mezze Selection",
      description: "A curated selection of hummus, baba ganoush, tabbouleh, dolmas, and fresh pita bread - perfect for sharing.",
      price: "$22.99",
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      chef: "Chef Layla",
      chefNote: "Each dip is made fresh daily using traditional family recipes",
      dietary: ["Vegetarian", "Vegan Options", "Fresh"],
      prepTime: "8 min",
      spiceLevel: 1,
      isSignature: false
    },
    {
      id: 4,
      name: "Mixed Grill Supreme",
      description: "The ultimate feast featuring lamb, chicken, and beef kebabs with rice pilaf, grilled tomatoes, and our house-made sauces.",
      price: "$28.99",
      image: "https://images.pexels.com/photos/5474641/pexels-photo-5474641.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      chef: "Chef Hassan",
      chefNote: "Perfect for meat lovers - our most popular sharing platter",
      dietary: ["Halal", "High Protein"],
      prepTime: "18 min",
      spiceLevel: 2,
      isSignature: true
    },
    {
      id: 5,
      name: "Falafel Garden Bowl",
      description: "Crispy homemade falafel served over fresh greens, quinoa, roasted vegetables, and tahini dressing.",
      price: "$14.99",
      image: "https://images.pexels.com/photos/6107787/pexels-photo-6107787.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      chef: "Chef Nadia",
      chefNote: "Made from scratch daily with chickpeas, herbs, and love",
      dietary: ["Vegan", "Gluten Free", "High Fiber"],
      prepTime: "10 min",
      spiceLevel: 1,
      isSignature: false
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % signatureDishes.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [isAutoPlaying, signatureDishes.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % signatureDishes.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + signatureDishes.length) % signatureDishes.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const renderSpiceLevel = (level) => {
    return Array.from({ length: 3 }, (_, i) => (
      <Icon
        key={i}
        name="Flame"
        size={16}
        className={i < level ? 'text-conversion fill-current' : 'text-gray-300'}
      />
    ));
  };

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Signature Dishes
          </h2>
          <p className="font-body text-xl text-text-secondary max-w-3xl mx-auto">
            Discover our most beloved creations, each crafted with authentic Mediterranean traditions and the finest ingredients
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Main Carousel */}
          <div className="overflow-hidden rounded-2xl shadow-warm-lg" ref={carouselRef}>
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {signatureDishes.map((dish) => (
                <div key={dish.id} className="w-full flex-shrink-0">
                  <div className="grid lg:grid-cols-2 gap-0 bg-background">
                    {/* Image Section */}
                    <div className="relative h-96 lg:h-[600px] overflow-hidden">
                      <Image
                        src={dish.image}
                        alt={dish.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-text-primary/60 via-transparent to-transparent"></div>
                      
                      {/* Signature Badge */}
                      {dish.isSignature && (
                        <div className="absolute top-6 left-6 bg-accent text-white px-4 py-2 rounded-full font-cta font-semibold text-sm">
                          <Icon name="Star" size={16} className="mr-1 fill-current" />
                          Signature Dish
                        </div>
                      )}

                      {/* Price Badge */}
                      <div className="absolute bottom-6 right-6 bg-primary text-white px-6 py-3 rounded-full font-cta font-bold text-xl">
                        {dish.price}
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <div className="mb-6">
                        <h3 className="font-headline text-3xl lg:text-4xl font-bold text-text-primary mb-4">
                          {dish.name}
                        </h3>
                        <p className="font-body text-lg text-text-secondary leading-relaxed mb-6">
                          {dish.description}
                        </p>
                      </div>

                      {/* Chef's Note */}
                      <div className="bg-primary-50 rounded-lg p-6 mb-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon name="ChefHat" size={24} className="text-white" />
                          </div>
                          <div>
                            <h4 className="font-cta font-semibold text-primary mb-2">{dish.chef}</h4>
                            <p className="font-accent text-text-secondary italic">"{dish.chefNote}"</p>
                          </div>
                        </div>
                      </div>

                      {/* Dish Details */}
                      <div className="grid grid-cols-2 gap-6 mb-8">
                        <div>
                          <div className="flex items-center space-x-2 mb-2">
                            <Icon name="Clock" size={18} className="text-trust" />
                            <span className="font-body font-medium text-text-primary">Prep Time</span>
                          </div>
                          <p className="text-text-secondary">{dish.prepTime}</p>
                        </div>
                        <div>
                          <div className="flex items-center space-x-2 mb-2">
                            <Icon name="Flame" size={18} className="text-conversion" />
                            <span className="font-body font-medium text-text-primary">Spice Level</span>
                          </div>
                          <div className="flex space-x-1">
                            {renderSpiceLevel(dish.spiceLevel)}
                          </div>
                        </div>
                      </div>

                      {/* Dietary Tags */}
                      <div className="mb-8">
                        <div className="flex flex-wrap gap-2">
                          {dish.dietary.map((tag, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-trust-100 text-trust-700 rounded-full text-sm font-medium"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-4">
                        <Link
                          to="/order-online-streamlined-experience"
                          className="btn-conversion flex-1 text-center"
                        >
                          <Icon name="ShoppingBag" size={20} className="mr-2" />
                          Order Now
                        </Link>
                        <Link
                          to="/interactive-menu-culinary-discovery"
                          className="btn-primary flex-1 text-center"
                        >
                          <Icon name="Eye" size={20} className="mr-2" />
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center mt-8 space-x-3">
            {signatureDishes.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-4 h-4 rounded-full transition-organic ${
                  index === currentIndex 
                    ? 'bg-primary shadow-warm' 
                    : 'bg-primary-200 hover:bg-primary-300'
                }`}
                aria-label={`Go to dish ${index + 1}`}
              />
            ))}
          </div>

          {/* Arrow Controls */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white shadow-warm rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-organic"
            aria-label="Previous dish"
          >
            <Icon name="ChevronLeft" size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white shadow-warm rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-organic"
            aria-label="Next dish"
          >
            <Icon name="ChevronRight" size={24} />
          </button>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <Link
            to="/interactive-menu-culinary-discovery"
            className="inline-flex items-center space-x-2 text-primary hover:text-primary-600 font-cta font-semibold text-lg transition-organic"
          >
            <span>Explore Our Complete Menu</span>
            <Icon name="ArrowRight" size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SignatureDishes;